// src/routes/non-infosec-risk/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import { 
  nonInfoSecRiskStore, 
  nonInfoSecRiskActions, 
  getNonInfoSecRiskLevel,
  calculateNonInfoSecRiskScore 
} from '../../stores/nonInfoSecRiskStore';
import { assetStore, getUniqueAssetGroups, getGroupCategoryCount } from '../../stores/assetStore';
import { getAssetDisplayName } from '../../utils/assetDisplayName';
import { riskMatrixStore, riskMatrixActions } from '../../stores/riskMatrixStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { formatDate } from '../../utils/dateUtils';
import { exportToCSV } from '../../utils/exportUtils';
import type { NonInfoSecRisk, NonInfoSecRiskFormData, NonInfoSecLikelihood, NonInfoSecSeverity } from '../../types/nonInfoSecRisk';

export const Route = createFileRoute('/non-infosec-risk/')({
  component: NonInfoSecRiskAssessmentModule,
});

function NonInfoSecRiskAssessmentModule() {
  const riskSnap = useSnapshot(nonInfoSecRiskStore);
  const assetSnap = useSnapshot(assetStore);
  const matrixSnap = useSnapshot(riskMatrixStore);
  const { showToast, ToastContainer } = useToast();
  
  // Get labels from store
  const likelihoodLabels = matrixSnap.nonInfoSec.likelihoodLabels.map(l => l.label);
  const severityLabels = matrixSnap.nonInfoSec.severityLabels.map(s => s.label);
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState<NonInfoSecRisk | null>(null);
  const [viewingRisk, setViewingRisk] = useState<NonInfoSecRisk | null>(null);

  const initialFormData: NonInfoSecRiskFormData = {
    referenceNumber: '',
    riskTitle: '',
    riskDescription: '',
    riskCategory: [],
    riskOwner: '',
    asset: '',
    assetReferenceNumber: '',
    assetSku: '',
    assetGroup: '',
    assetGroupCount: 0,
    assetLinkType: 'group' as const,
    cause: '',
    consequence: '',
    ucLikelihood: 'Likely',
    ucSeverity: 'Moderate',
    treatment: 'Treat the risk',
    treatmentDetail: '',
    mitigation: '',
    mitigationDescription: '',
    customMitigation: '',
    managedLikelihood: 'Unlikely',
    managedSeverity: 'Minor',
    opportunities: '',
    actions: '',
    comments: '',
    controlStatus: 'Not Started',
    dateCompleted: '',
    maxReviewPeriod: '12',
    lastDateReviewed: '',
    notes: '',
  };

  const [formData, setFormData] = useState<NonInfoSecRiskFormData>(initialFormData);

  // Filtered risks
  const filteredRisks = useMemo(() => {
    return riskSnap.risks.filter((risk) => {
      const matchesSearch = !riskSnap.searchTerm || 
        risk.riskTitle.toLowerCase().includes(riskSnap.searchTerm.toLowerCase()) ||
        risk.riskDescription.toLowerCase().includes(riskSnap.searchTerm.toLowerCase()) ||
        risk.referenceNumber?.toLowerCase().includes(riskSnap.searchTerm.toLowerCase());
      const matchesCategory = riskSnap.categoryFilter === 'all' || risk.riskCategory.includes(riskSnap.categoryFilter);
      const matchesStatus = riskSnap.statusFilter === 'all' || risk.controlStatus === riskSnap.statusFilter;
      const matchesTreatment = riskSnap.treatmentFilter === 'all' || risk.treatment === riskSnap.treatmentFilter;
      return matchesSearch && matchesCategory && matchesStatus && matchesTreatment;
    });
  }, [riskSnap.risks, riskSnap.searchTerm, riskSnap.categoryFilter, riskSnap.statusFilter, riskSnap.treatmentFilter]);

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const sortedRisks = useMemo(() => {
    if (!sortColumn) return filteredRisks;
    return [...filteredRisks].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortColumn) {
        case 'ref': aVal = a.referenceNumber || ''; bVal = b.referenceNumber || ''; break;
        case 'title': aVal = a.riskTitle || ''; bVal = b.riskTitle || ''; break;
        case 'category': aVal = a.riskCategory.join(', '); bVal = b.riskCategory.join(', '); break;
        case 'uncontrolled': aVal = calculateNonInfoSecRiskScore(a.ucLikelihood, a.ucSeverity); bVal = calculateNonInfoSecRiskScore(b.ucLikelihood, b.ucSeverity); break;
        case 'managed': aVal = calculateNonInfoSecRiskScore(a.managedLikelihood, a.managedSeverity); bVal = calculateNonInfoSecRiskScore(b.managedLikelihood, b.managedSeverity); break;
        case 'treatment': aVal = a.treatment || ''; bVal = b.treatment || ''; break;
        case 'status': aVal = a.controlStatus || ''; bVal = b.controlStatus || ''; break;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredRisks, sortColumn, sortDirection]);

  // Stats
  const stats = useMemo(() => {
    const risks = riskSnap.risks;
    return {
      total: risks.length,
      high: risks.filter(r => getNonInfoSecRiskLevel(r.managedLikelihood, r.managedSeverity).level === 'High').length,
      medium: risks.filter(r => getNonInfoSecRiskLevel(r.managedLikelihood, r.managedSeverity).level === 'Medium').length,
      low: risks.filter(r => getNonInfoSecRiskLevel(r.managedLikelihood, r.managedSeverity).level === 'Low').length,
    };
  }, [riskSnap.risks]);

  // Category stats
  const categoryStats = useMemo(() => {
    return riskSnap.config.riskCategories.map(cat => ({
      name: cat,
      count: riskSnap.risks.filter(r => r.riskCategory.includes(cat)).length,
      high: riskSnap.risks.filter(r => r.riskCategory.includes(cat) &&
        getNonInfoSecRiskLevel(r.managedLikelihood, r.managedSeverity).level === 'High').length
    }));
  }, [riskSnap.risks, riskSnap.config.riskCategories]);

  // Asset group tiles for flat tile display
  const [showAssetGroupModal, setShowAssetGroupModal] = useState(false);
  const [selectedAssetGroup, setSelectedAssetGroup] = useState('');
  const [preFilledFromTile, setPreFilledFromTile] = useState(false);
  const [showAssetGroupTiles, setShowAssetGroupTiles] = useState(false);
  const [showCategoryCards, setShowCategoryCards] = useState(false);

  const assetGroupTiles = useMemo(() => {
    const groupMap = new Map<string, typeof assetSnap.assets[number][]>();
    assetSnap.assets.forEach(asset => {
      if (asset.assetGroup) {
        const existing = groupMap.get(asset.assetGroup) || [];
        existing.push(asset);
        groupMap.set(asset.assetGroup, existing);
      }
    });
    return Array.from(groupMap.entries())
      .map(([groupName, assets]) => ({
        groupName,
        count: assets.length,
        assets,
        existingRisks: riskSnap.risks.filter(r => r.assetGroup === groupName).length,
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [assetSnap.assets, riskSnap.risks]);

  const assetsForSelectedGroup = useMemo(() => {
    if (!selectedAssetGroup) return [];
    return assetSnap.assets.filter(a => a.assetGroup === selectedAssetGroup);
  }, [selectedAssetGroup, assetSnap.assets]);

  const handleGroupTileClick = (groupName: string) => {
    setSelectedAssetGroup(groupName);
    setShowAssetGroupModal(true);
  };

  const handleAssessGroup = () => {
    setShowAssetGroupModal(false);
    setEditingRisk(null);
    setPreFilledFromTile(true);
    const count = assetsForSelectedGroup.length;
    setFormData({
      ...initialFormData,
      assetGroup: selectedAssetGroup,
      assetGroupCount: count,
      assetLinkType: 'group' as const,
      asset: '',
      assetReferenceNumber: '',
      assetSku: '',
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingRisk(null);
    setPreFilledFromTile(false);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (risk: NonInfoSecRisk) => {
    setEditingRisk(risk);
    setPreFilledFromTile(false);
    setFormData({ ...risk });
    setShowModal(true);
  };

  const openViewModal = (risk: NonInfoSecRisk) => {
    setViewingRisk(risk);
    setShowViewModal(true);
  };

  const handleSave = async () => {
    if (!formData.riskTitle || formData.riskCategory.length === 0) {
      showToast('Please fill in required fields (Risk Title and Category)', 'error');
      return;
    }

    // Validate dates
    if (formData.dateCompleted && formData.lastDateReviewed) {
      const implementedDate = new Date(formData.dateCompleted);
      const reviewedDate = new Date(formData.lastDateReviewed);
      if (reviewedDate < implementedDate) {
        showToast('Last Date Reviewed must be on or after the implementation date', 'error');
        return;
      }
    }

    try {
      if (editingRisk) {
        const updateData: Partial<NonInfoSecRisk> = {
          ...formData,
          assetGroup: formData.assetGroup || '',
          assetGroupCount: formData.assetGroupCount || 0,
          assetLinkType: formData.assetLinkType || 'group',
          updatedAt: editingRisk.updatedAt,
          ucRiskLevel: '',
          ucRiskScore: 0,
          managedRiskLevel: '',
          managedRiskScore: 0,
        };
        await nonInfoSecRiskActions.updateRisk(editingRisk.id, updateData);
        showToast('Risk updated successfully', 'success');
      } else {
        const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const newRisk: NonInfoSecRisk = {
          ...formData,
          id: tempId,
          referenceNumber: '',
          assetGroup: formData.assetGroup || '',
          assetGroupCount: formData.assetGroupCount || 0,
          assetLinkType: formData.assetLinkType || 'group',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          ucRiskLevel: '',
          ucRiskScore: 0,
          managedRiskLevel: '',
          managedRiskScore: 0,
        };
        await nonInfoSecRiskActions.addRisk(newRisk);
        showToast('Risk added successfully', 'success');
      }
      setShowModal(false);
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        showToast('This risk was modified by another user. The latest version has been loaded.', 'error');
      } else {
        showToast('Failed to save risk — please try again', 'error');
      }
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this risk?')) {
      nonInfoSecRiskActions.deleteRisk(id);
      showToast('Risk deleted successfully', 'success');
    }
  };

  const handleDuplicate = (risk: NonInfoSecRisk) => {
    setEditingRisk(null);
    setFormData({
      ...risk,
      id: undefined,
      riskTitle: `${risk.riskTitle} (Copy)`,
      referenceNumber: '',
    });
    setShowModal(true);
    showToast('Risk duplicated - modify and save', 'info');
  };

  const handleExport = () => {
    const data = filteredRisks.map(risk => ({
      'Reference': risk.referenceNumber,
      'Risk Title': risk.riskTitle,
      'Description': risk.riskDescription,
      'Category': risk.riskCategory.join(', '),
      'Risk Owner': risk.riskOwner,
      'Related Asset': risk.asset,
      'Asset Reference': risk.assetReferenceNumber,
      'Asset SKU': risk.assetSku,
      'Asset Group': risk.assetGroup || '',
      'Asset Group Count': risk.assetGroupCount || 0,
      'Asset Link Type': risk.assetLinkType || 'single',
      'Cause': risk.cause,
      'Consequence': risk.consequence,
      'UC Likelihood': risk.ucLikelihood,
      'UC Severity': risk.ucSeverity,
      'UC Score': calculateNonInfoSecRiskScore(risk.ucLikelihood, risk.ucSeverity),
      'Treatment': risk.treatment,
      'Mitigation': risk.mitigation,
      'Managed Likelihood': risk.managedLikelihood,
      'Managed Severity': risk.managedSeverity,
      'Managed Score': calculateNonInfoSecRiskScore(risk.managedLikelihood, risk.managedSeverity),
      'Status': risk.controlStatus,
      'Last Reviewed': risk.lastDateReviewed,
      'Notes': risk.notes,
    }));
    exportToCSV(data, 'non-infosec-risks');
    showToast('Export complete', 'success');
  };

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <ToastContainer />

      <ModuleHeader
        title="Non-InfoSec Risk Assessment"
        subtitle="Business risk management and treatment planning"
      />

      <div className="flex-shrink-0 px-6 pt-4 pb-0">
        {/* Action Bar + Stats */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button onClick={openAddModal} className="bg-gradient-to-r from-emerald-600 to-teal-600">
                <Icon name="Plus" size={16} className="mr-2" />
                Add Risk
              </Button>
              <Button variant="secondary" onClick={handleExport}>
                <Icon name="Download" size={16} className="mr-2" />
                Export
              </Button>
              <div className="hidden md:flex items-center gap-3 ml-2 pl-3 border-l border-gray-200">
                <button onClick={() => { nonInfoSecRiskActions.setStatusFilter('all'); nonInfoSecRiskActions.setCategoryFilter('all'); }} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'all' && riskSnap.categoryFilter === 'all' ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Total</p><p className="text-lg font-bold">{stats.total}</p>
                </button>
                <button onClick={() => nonInfoSecRiskActions.setStatusFilter('all')} className="text-center px-2 py-1 rounded transition hover:bg-gray-50">
                  <p className="text-xs text-gray-500">High</p><p className="text-lg font-bold text-red-600">{stats.high}</p>
                </button>
                <button onClick={() => nonInfoSecRiskActions.setStatusFilter('all')} className="text-center px-2 py-1 rounded transition hover:bg-gray-50">
                  <p className="text-xs text-gray-500">Medium</p><p className="text-lg font-bold text-yellow-600">{stats.medium}</p>
                </button>
                <button onClick={() => nonInfoSecRiskActions.setStatusFilter('all')} className="text-center px-2 py-1 rounded transition hover:bg-gray-50">
                  <p className="text-xs text-gray-500">Low</p><p className="text-lg font-bold text-green-600">{stats.low}</p>
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search risks..." value={riskSnap.searchTerm} onChange={(e) => nonInfoSecRiskActions.setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
              </div>
              <select value={riskSnap.treatmentFilter} onChange={(e) => nonInfoSecRiskActions.setTreatmentFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                <option value="all">All Treatments</option>
                {riskSnap.config.treatments.map(t => (
                  <option key={t.treatment} value={t.treatment}>{t.treatment}</option>
                ))}
              </select>
              <select value={riskSnap.statusFilter} onChange={(e) => nonInfoSecRiskActions.setStatusFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                <option value="all">All Statuses</option>
                {riskSnap.config.controlStatusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Asset Group Tiles — collapsible */}
        {assetGroupTiles.length > 0 && (
          <div className="mb-2">
            <button
              onClick={() => setShowAssetGroupTiles(!showAssetGroupTiles)}
              className="flex items-center gap-2 w-full text-left group"
            >
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${showAssetGroupTiles ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <h2 className="text-sm font-semibold text-gray-700">Asset Groups</h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{assetGroupTiles.length}</span>
              <span className="text-xs text-gray-400 ml-auto group-hover:text-gray-600">Click to {showAssetGroupTiles ? 'collapse' : 'expand'}</span>
            </button>
            {showAssetGroupTiles && (
              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2 mt-2">
                {assetGroupTiles.map((tile, index) => (
                  <motion.div
                    key={tile.groupName}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleGroupTileClick(tile.groupName)}
                    className="bg-white rounded-lg shadow-sm border-l-3 border-emerald-500 p-2 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-semibold text-gray-900 text-xs truncate mb-1">{tile.groupName}</h3>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400">Assets:</span>
                      <span className="font-medium">{tile.count}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400">Risks:</span>
                      <span className="font-medium text-emerald-600">{tile.existingRisks}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Category Cards — collapsible */}
        <div className="mb-2">
          <button
            onClick={() => setShowCategoryCards(!showCategoryCards)}
            className="flex items-center gap-2 w-full text-left group"
          >
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showCategoryCards ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <h2 className="text-sm font-semibold text-gray-700">Risks by Category</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{categoryStats.length}</span>
            <span className="text-xs text-gray-400 ml-auto group-hover:text-gray-600">Click to {showCategoryCards ? 'collapse' : 'expand'}</span>
          </button>
          {showCategoryCards && (
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2 mt-2">
              {categoryStats.map((cat, index) => {
                const colorSchemes = [
                  'from-blue-500 to-cyan-500',
                  'from-purple-600 to-indigo-600',
                  'from-green-500 to-emerald-500',
                  'from-amber-500 to-orange-500',
                  'from-pink-500 to-rose-500',
                  'from-teal-500 to-cyan-500',
                  'from-red-500 to-orange-500'
                ];

                return (
                  <motion.div
                    key={cat.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => nonInfoSecRiskActions.setCategoryFilter(
                      riskSnap.categoryFilter === cat.name ? 'all' : cat.name
                    )}
                    className={`bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${
                      riskSnap.categoryFilter === cat.name ? 'ring-2 ring-emerald-500' : ''
                    }`}
                  >
                    <div className={`bg-gradient-to-r ${colorSchemes[index % colorSchemes.length]} px-2 py-1 text-white`}>
                      <h3 className="font-semibold text-[11px] truncate">{cat.name}</h3>
                    </div>
                    <div className="p-1.5 text-center">
                      <p className="text-base font-bold text-gray-800">{cat.count}</p>
                      {cat.high > 0 && (
                        <span className="text-[10px] text-red-600 font-semibold">{cat.high} high</span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        </div>

        {/* Risks Table — fills remaining space, scrolls independently */}
        <div className="flex-1 min-h-0 px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm h-full overflow-auto">
            <table className="w-full min-w-[900px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  {([
                    { key: 'ref', label: 'Ref' },
                    { key: 'title', label: 'Risk Title' },
                    { key: 'category', label: 'Category' },
                    { key: 'uncontrolled', label: 'Uncontrolled' },
                    { key: 'managed', label: 'Managed' },
                    { key: 'treatment', label: 'Treatment' },
                    { key: 'status', label: 'Status' },
                  ] as const).map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortColumn === col.key ? (
                          <svg className="w-3.5 h-3.5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                            {sortDirection === 'asc'
                              ? <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 6.414l-3.293 3.293a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              : <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 13.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />}
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedRisks.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      {riskSnap.risks.length === 0
                        ? 'No risks added yet. Click "Add Risk" to get started.'
                        : 'No risks match your filters.'}
                    </td>
                  </tr>
                ) : (
                  sortedRisks.map((risk) => {
                    const ucRisk = getNonInfoSecRiskLevel(risk.ucLikelihood, risk.ucSeverity);
                    const mRisk = getNonInfoSecRiskLevel(risk.managedLikelihood, risk.managedSeverity);
                    
                    return (
                      <tr key={risk.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">{risk.referenceNumber}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openViewModal({...risk, riskCategory: [...risk.riskCategory]})}
                            className="text-sm font-medium text-emerald-600 hover:text-emerald-800 text-left"
                          >
                            {risk.riskTitle}
                          </button>
                          {risk.riskDescription && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{risk.riskDescription}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{risk.riskCategory.join(', ')}</td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: ucRisk.color }}
                          >
                            {ucRisk.score}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: mRisk.color }}
                          >
                            {mRisk.score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {risk.treatment.replace(' the risk', '')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            risk.controlStatus === 'Implemented' ? 'bg-green-100 text-green-800' :
                            risk.controlStatus === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            risk.controlStatus === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {risk.controlStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditModal({...risk, riskCategory: [...risk.riskCategory]})}
                              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition"
                              title="Edit"
                            >
                              <Icon name="Edit" size={16} />
                            </button>
                            <button
                              onClick={() => handleDuplicate({...risk, riskCategory: [...risk.riskCategory]})}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Duplicate"
                            >
                              <Icon name="Copy" size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(risk.id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition"
                              title="Delete"
                            >
                              <Icon name="Trash" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      {/* Asset Group Modal */}
      <Modal isOpen={showAssetGroupModal} onClose={() => setShowAssetGroupModal(false)} title={`${selectedAssetGroup} Assets`} subtitle={`${assetsForSelectedGroup.length} ${assetsForSelectedGroup.length === 1 ? 'asset' : 'assets'} in this group`} size='lg' gradient='from-emerald-600 to-teal-600'>
        <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
          {assetsForSelectedGroup.length === 0 ? (<p className="text-center text-gray-500 py-8">No assets in this group</p>) : (
            assetsForSelectedGroup.map((asset) => {
              const displayName = getAssetDisplayName(asset);
              return (
                <div key={asset.id} className="p-3 rounded-lg border bg-white border-gray-200">
                  <div className="flex justify-between items-center">
                    <div><p className="font-medium text-gray-900 text-sm">{displayName}</p><p className="text-xs text-gray-500">{asset.referenceNumber} • {asset.location || 'No location'}</p></div>
                    <span className="text-xs text-gray-500">{asset.owner || 'No owner'}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {assetsForSelectedGroup.length > 0 && (
          <button
            onClick={handleAssessGroup}
            className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md"
          >
            Assess this Group ({assetsForSelectedGroup.length} {assetsForSelectedGroup.length === 1 ? 'asset' : 'assets'})
          </button>
        )}
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Risk Details"
        size="lg"
      >
        {viewingRisk && <RiskDetails risk={viewingRisk} />}
        <div className="flex gap-3 pt-4 border-t mt-4">
          <Button onClick={() => {
            setShowViewModal(false);
            openEditModal(viewingRisk!);
          }}>
            Edit Risk
          </Button>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </div>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingRisk ? 'Edit Risk' : 'Add New Risk'}
        size="xl"
      >
        <RiskForm
          formData={formData}
          setFormData={setFormData}
          config={riskSnap.config}
          onSave={handleSave}
          onCancel={() => setShowModal(false)}
          isEditing={!!editingRisk}
          preFilledFromTile={preFilledFromTile}
        />
      </Modal>
      </div>
    </div>
  );
}

// Risk Matrix Component - No numbers, better spacing for headers, with descriptions and RAG key
function RiskMatrix({ 
  selectedLikelihood, 
  selectedSeverity, 
  onSelect, 
  title 
}: { 
  selectedLikelihood: string; 
  selectedSeverity: string; 
  onSelect: (likelihood: string, severity: string) => void;
  title: string;
}) {
  const matrixSnap = useSnapshot(riskMatrixStore);
  const [showLikelihoodDesc, setShowLikelihoodDesc] = useState(false);
  const [showSeverityDesc, setShowSeverityDesc] = useState(false);

  // Get labels from store
  const likelihoodLabels = matrixSnap.nonInfoSec.likelihoodLabels.map(l => l.label);
  const severityLabels = matrixSnap.nonInfoSec.severityLabels.map(s => s.label);

  const getRiskColorForCell = (likelihoodIdx: number, severityIdx: number): string => {
    const color = matrixSnap.nonInfoSec.colors[likelihoodIdx][severityIdx];
    return riskMatrixActions.getColorClass(color);
  };

  const isSelected = (likelihoodIdx: number, severityIdx: number): boolean => {
    return likelihoodLabels[likelihoodIdx] === selectedLikelihood && 
           severityLabels[severityIdx] === selectedSeverity;
  };

  return (
    <div className="mb-4">
      <p className="text-sm text-gray-600 mb-2 italic">
        Click on the matrix to select likelihood and severity. <button 
          type="button"
          onClick={() => {
            setShowLikelihoodDesc(!showLikelihoodDesc);
            setShowSeverityDesc(!showSeverityDesc);
          }}
          className="text-emerald-600 hover:text-emerald-800 underline font-medium"
        >
          {(showLikelihoodDesc || showSeverityDesc) ? 'Hide descriptions' : 'Click here to see descriptions below'}
        </button>
      </p>
      <div className="flex gap-6 items-start">
        {/* Matrix Table */}
        <div className="flex-1 ">
          <div className="bg-white border-2 border-gray-300 rounded-xl shadow-md overflow-hidden ">
            <table className="w-full table-fixed">
              <thead>
                <tr>
                  <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                  <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '110px' }}></th>
                  <th colSpan={5} className="py-4 px-2 text-center border-b-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
                    <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← Severity →</span>
                  </th>
                </tr>
                <tr>
                  <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                  <th className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '110px', height: '90px' }}>
                  </th>
                  {severityLabels.map((sev) => (
                    <th key={sev} className="border-b border-l border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 px-2" style={{ height: '90px' }}>
                      <div className="flex items-center justify-center h-full">
                        <div className="transform -rotate-45 origin-center whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-800">{sev}</span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {likelihoodLabels.map((lik, likIdx) => (
                  <tr key={lik} className="group">
                    {likIdx === 0 && (
                      <td rowSpan={5} className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-center align-middle" style={{ width: '50px' }}>
                        <div className="flex items-center justify-center h-full">
                          <div className="transform -rotate-90 whitespace-nowrap">
                            <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← Likelihood →</span>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="border-r-2 border-t border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-right" style={{ height: '70px', width: '110px' }}>
                      <div className="flex items-center justify-end h-full px-2">
                        <span className="text-sm font-semibold text-gray-900 text-right leading-tight" style={{ wordBreak: 'break-word', maxWidth: '100px' }}>{lik}</span>
                      </div>
                    </td>
                    {severityLabels.map((sev, sevIdx) => (
                      <td key={`${lik}-${sev}`} className="p-2 border border-gray-300 bg-white group-hover:bg-gray-50/30 transition-colors" style={{ height: '70px', width: '110px' }}>
                        <div className="flex items-center justify-center h-full">
                          <button
                            type="button"
                            onClick={() => onSelect(lik, sev)}
                            className={`
                              w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer
                              flex items-center justify-center
                              hover:scale-110 hover:shadow-2xl hover:z-10 active:scale-95
                              ${getRiskColorForCell(likIdx, sevIdx)}
                              ${isSelected(likIdx, sevIdx) ? 'ring-4 ring-offset-2 ring-emerald-500 ring-opacity-60' : ''}
                            `}
                            title={`${lik} x ${sev}`}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* RAG Key */}
        <div className="flex-shrink-0 w-56">
          <div className="bg-white border-2 border-gray-300 rounded-xl shadow-md p-5 sticky top-4">
            <h5 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Risk Level Key</h5>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Low</p>
                  <p className="text-xs text-gray-600">Green</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Medium</p>
                  <p className="text-xs text-gray-600">Amber</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">High</p>
                  <p className="text-xs text-gray-600">Red</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Likelihood Descriptions */}
      {showLikelihoodDesc && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h5 className="text-xs font-semibold text-gray-700 mb-2">Likelihood Definitions:</h5>
          <div className="space-y-1">
            {matrixSnap.nonInfoSec.likelihoodLabels.map((item) => (
              <div key={item.label} className="text-xs">
                <span className="font-medium text-gray-700">{item.label}:</span>{' '}
                <span className="text-gray-600">{item.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Severity Descriptions */}
      {showSeverityDesc && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h5 className="text-xs font-semibold text-gray-700 mb-2">Severity Definitions:</h5>
          <div className="space-y-1">
            {matrixSnap.nonInfoSec.severityLabels.map((item) => (
              <div key={item.label} className="text-xs">
                <span className="font-medium text-gray-700">{item.label}:</span>{' '}
                <span className="text-gray-600">{item.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedLikelihood && selectedSeverity && (
        <p className="text-sm text-gray-600 mt-3">
          Selected: <span className="font-medium">{selectedLikelihood}</span> x <span className="font-medium">{selectedSeverity}</span> = {
            calculateNonInfoSecRiskScore(selectedLikelihood, selectedSeverity)
          }
        </p>
      )}
    </div>
  );
}

// Risk Form Component
function RiskForm({
  formData,
  setFormData,
  config,
  onSave,
  onCancel,
  isEditing,
  preFilledFromTile = false
}: {
  formData: NonInfoSecRiskFormData;
  setFormData: (data: NonInfoSecRiskFormData) => void;
  config: any;
  onSave: () => void;
  onCancel: () => void;
  isEditing: boolean;
  preFilledFromTile?: boolean;
}) {
  const assetSnap = useSnapshot(assetStore);
  const assetFieldsReadOnly = preFilledFromTile;
  const ucRisk = getNonInfoSecRiskLevel(formData.ucLikelihood, formData.ucSeverity);
  const mRisk = getNonInfoSecRiskLevel(formData.managedLikelihood, formData.managedSeverity);

  // Calculate review by date
  const reviewByDate = useMemo(() => {
    if (!formData.lastDateReviewed || !formData.maxReviewPeriod) return '';
    const date = new Date(formData.lastDateReviewed);
    date.setMonth(date.getMonth() + parseInt(formData.maxReviewPeriod));
    return date.toISOString().split('T')[0];
  }, [formData.lastDateReviewed, formData.maxReviewPeriod]);

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risk Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.riskTitle}
              onChange={(e) => setFormData({ ...formData, riskTitle: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                !formData.riskTitle ? 'border-gray-300' : 'border-gray-300'
              }`}
              placeholder="Brief title describing the risk"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Description</label>
            <textarea
              value={formData.riskDescription}
              onChange={(e) => setFormData({ ...formData, riskDescription: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              rows={2}
              placeholder="Detailed description of the risk"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risk Category <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {config.riskCategories.map((cat: string) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.riskCategory.includes(cat)}
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...formData.riskCategory, cat]
                        : formData.riskCategory.filter((c: string) => c !== cat);
                      setFormData({ ...formData, riskCategory: updated });
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{cat}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Owner</label>
            <input
              type="text"
              value={formData.riskOwner}
              onChange={(e) => setFormData({ ...formData, riskOwner: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Person responsible"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Related Asset Group</label>

            {/* Legacy single-asset info for editing old risks */}
            {isEditing && formData.assetLinkType === 'single' && formData.asset && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-600 font-medium mb-1">Originally linked to single asset</p>
                <p className="text-sm text-blue-900">{formData.asset}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Group</label>
                {assetFieldsReadOnly ? (
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700">{formData.assetGroup || 'None'}</div>
                ) : (
                  <input
                    type="text"
                    value={formData.assetGroup || ''}
                    onChange={(e) => {
                      const groupName = e.target.value;
                      const count = groupName ? getGroupCategoryCount(groupName) : 0;
                      setFormData({ ...formData, assetGroup: groupName, assetGroupCount: count, assetLinkType: 'group' });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. Laptops, Servers, Network Equipment"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group Count</label>
                <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600">
                  {formData.assetGroupCount || 0} {(formData.assetGroupCount || 0) === 1 ? 'asset' : 'assets'}
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cause</label>
            <input
              type="text"
              value={formData.cause}
              onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="What could cause this risk"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Consequence</label>
            <input
              type="text"
              value={formData.consequence}
              onChange={(e) => setFormData({ ...formData, consequence: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Impact if risk materializes"
            />
          </div>
        </div>
      </div>

      {/* Uncontrolled Risk Assessment */}
      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
        <h3 className="text-sm font-semibold text-red-700 mb-3">Uncontrolled Risk Assessment</h3>
        <p className="text-xs text-gray-500 mb-3">Click on the matrix to select likelihood and severity</p>
        <RiskMatrix
          selectedLikelihood={formData.ucLikelihood}
          selectedSeverity={formData.ucSeverity}
          onSelect={(likelihood, severity) => setFormData({
            ...formData,
            ucLikelihood: likelihood as NonInfoSecLikelihood,
            ucSeverity: severity as NonInfoSecSeverity
          })}
          title="Uncontrolled Risk Level"
        />
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-gray-600">Risk Level:</span>
          <span 
            className="px-3 py-1 rounded-full text-sm font-medium text-white"
            style={{ backgroundColor: ucRisk.color }}
          >
            {ucRisk.level} ({ucRisk.score})
          </span>
        </div>
      </div>

      {/* Treatment */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Risk Treatment</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Strategy</label>
            <select
              value={formData.treatment}
              onChange={(e) => {
                const selectedTreatment = config.treatments.find((t: any) => t.treatment === e.target.value);
                setFormData({
                  ...formData,
                  treatment: e.target.value as any,
                  treatmentDetail: selectedTreatment?.detail || ''
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              {config.treatments.map((t: any) => (
                <option key={t.treatment} value={t.treatment}>{t.treatment}</option>
              ))}
            </select>
            {formData.treatmentDetail && (
              <p className="text-xs text-gray-500 mt-2 italic">{formData.treatmentDetail}</p>
            )}
          </div>

          {formData.treatment === 'Treat the risk' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mitigation Measure</label>
                <select
                  value={formData.mitigation}
                  onChange={(e) => {
                    const selectedMitigation = config.mitigations.find((m: any) => m.name === e.target.value);
                    setFormData({
                      ...formData,
                      mitigation: e.target.value,
                      mitigationDescription: selectedMitigation?.description || ''
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select mitigation...</option>
                  {config.mitigations.map((m: any) => (
                    <option key={m.name} value={m.name}>{m.name}</option>
                  ))}
                </select>
                {formData.mitigationDescription && (
                  <p className="text-xs text-gray-500 mt-2 italic">{formData.mitigationDescription}</p>
                )}
              </div>

              {formData.mitigation === 'Custom Mitigation' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Mitigation Description</label>
                  <textarea
                    value={formData.customMitigation}
                    onChange={(e) => setFormData({ ...formData, customMitigation: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                    placeholder="Describe your custom mitigation approach"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Managed Risk Assessment */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h3 className="text-sm font-semibold text-green-700 mb-3">Managed Risk Assessment</h3>
        <p className="text-xs text-gray-500 mb-3">Risk level after treatment is applied</p>
        <RiskMatrix
          selectedLikelihood={formData.managedLikelihood}
          selectedSeverity={formData.managedSeverity}
          onSelect={(likelihood, severity) => setFormData({
            ...formData,
            managedLikelihood: likelihood as NonInfoSecLikelihood,
            managedSeverity: severity as NonInfoSecSeverity
          })}
          title="Managed Risk Level"
        />
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-gray-600">Risk Level:</span>
          <span 
            className="px-3 py-1 rounded-full text-sm font-medium text-white"
            style={{ backgroundColor: mRisk.color }}
          >
            {mRisk.level} ({mRisk.score})
          </span>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Opportunities</label>
            <textarea
              value={formData.opportunities}
              onChange={(e) => setFormData({ ...formData, opportunities: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              rows={2}
              placeholder="Potential opportunities arising from this risk"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
            <textarea
              value={formData.actions}
              onChange={(e) => setFormData({ ...formData, actions: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              rows={2}
              placeholder="Specific actions to be taken"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              rows={2}
              placeholder="Additional comments"
            />
          </div>
        </div>
      </div>

      {/* Control Status */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Control Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={formData.controlStatus}
              onChange={(e) => setFormData({
                ...formData,
                controlStatus: e.target.value as any,
                dateCompleted: e.target.value !== 'Implemented' ? '' : formData.dateCompleted
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              {config.controlStatusOptions.map((status: string) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          {formData.controlStatus === 'Implemented' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Implemented</label>
              <input
                type="date"
                value={formData.dateCompleted}
                onChange={(e) => setFormData({ ...formData, dateCompleted: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Review Schedule */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Review Schedule</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Date Reviewed</label>
            <input
              type="date"
              value={formData.lastDateReviewed}
              onChange={(e) => setFormData({ ...formData, lastDateReviewed: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Review Period (Months)</label>
            <input
              type="number"
              value={formData.maxReviewPeriod}
              onChange={(e) => setFormData({ ...formData, maxReviewPeriod: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              min="1"
              max="36"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Review By Date</label>
            <input
              type="date"
              value={reviewByDate}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Notes</h3>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          rows={3}
          placeholder="Additional notes..."
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-4 border-t sticky bottom-0 bg-white">
        {(!formData.riskTitle || formData.riskCategory.length === 0) && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Please fill in all required fields (marked with *)
          </p>
        )}
        <div className="flex gap-3">
          <Button 
            onClick={onSave} 
            className={`flex-1 ${
              (!formData.riskTitle || formData.riskCategory.length === 0) 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-emerald-600 to-teal-600'
            }`}
            disabled={!formData.riskTitle || formData.riskCategory.length === 0}
          >
            {isEditing ? 'Update Risk' : 'Add Risk'}
          </Button>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// Risk Details Component
function RiskDetails({ risk }: { risk: NonInfoSecRisk }) {
  const ucRisk = getNonInfoSecRiskLevel(risk.ucLikelihood, risk.ucSeverity);
  const mRisk = getNonInfoSecRiskLevel(risk.managedLikelihood, risk.managedSeverity);

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-500">Reference Number</label>
          <p className="text-gray-900 font-mono">{risk.referenceNumber}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-500">Category</label>
          <p className="text-gray-900">{risk.riskCategory.length > 0 ? risk.riskCategory.join(', ') : '-'}</p>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-500">Risk Title</label>
        <p className="text-gray-900 font-semibold text-lg">{risk.riskTitle}</p>
      </div>

      {risk.riskDescription && (
        <div>
          <label className="text-sm font-medium text-gray-500">Description</label>
          <p className="text-gray-900">{risk.riskDescription}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {risk.riskOwner && (
          <div>
            <label className="text-sm font-medium text-gray-500">Risk Owner</label>
            <p className="text-gray-900">{risk.riskOwner}</p>
          </div>
        )}
        {risk.assetLinkType === 'group' ? (
          <>
            {risk.assetGroup && (
              <div>
                <label className="text-sm font-medium text-gray-500">Asset Group</label>
                <p className="text-gray-900">{risk.assetGroup}</p>
              </div>
            )}
            {risk.assetGroupCount > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Group Count</label>
                <p className="text-gray-900">{risk.assetGroupCount}</p>
              </div>
            )}
          </>
        ) : (
          <>
            {risk.asset && (
              <div>
                <label className="text-sm font-medium text-gray-500">Related Asset</label>
                <p className="text-gray-900">{risk.asset}</p>
              </div>
            )}
            {risk.assetReferenceNumber && (
              <div>
                <label className="text-sm font-medium text-gray-500">Asset Reference</label>
                <p className="text-gray-900 font-mono">{risk.assetReferenceNumber}</p>
              </div>
            )}
            {risk.assetSku && (
              <div>
                <label className="text-sm font-medium text-gray-500">Asset SKU</label>
                <p className="text-gray-900">{risk.assetSku}</p>
              </div>
            )}
          </>
        )}
        {risk.cause && (
          <div>
            <label className="text-sm font-medium text-gray-500">Cause</label>
            <p className="text-gray-900">{risk.cause}</p>
          </div>
        )}
        {risk.consequence && (
          <div>
            <label className="text-sm font-medium text-gray-500">Consequence</label>
            <p className="text-gray-900">{risk.consequence}</p>
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Risk Levels</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-sm text-gray-500 mb-1">Uncontrolled Risk</p>
            <p className="text-lg font-bold" style={{ color: ucRisk.color }}>
              {ucRisk.level} ({ucRisk.score})
            </p>
            <p className="text-sm text-gray-600">{risk.ucLikelihood} x {risk.ucSeverity}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm text-gray-500 mb-1">Managed Risk</p>
            <p className="text-lg font-bold" style={{ color: mRisk.color }}>
              {mRisk.level} ({mRisk.score})
            </p>
            <p className="text-sm text-gray-600">{risk.managedLikelihood} x {risk.managedSeverity}</p>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Treatment & Mitigation</h3>
        <p className="text-emerald-900 font-medium bg-emerald-50 p-3 rounded mb-3">{risk.treatment}</p>
        {risk.mitigation && (
          <div className="mt-3">
            <label className="text-sm font-medium text-gray-500">Mitigation</label>
            <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.mitigation}</p>
            {risk.mitigationDescription && (
              <p className="text-sm text-gray-600 mt-1">{risk.mitigationDescription}</p>
            )}
          </div>
        )}
        {risk.customMitigation && (
          <div className="mt-3">
            <label className="text-sm font-medium text-gray-500">Custom Mitigation</label>
            <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.customMitigation}</p>
          </div>
        )}
      </div>

      {(risk.opportunities || risk.actions || risk.comments) && (
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-4">Additional Information</h3>
          {risk.opportunities && (
            <div className="mb-3">
              <label className="text-sm font-medium text-gray-500">Opportunities</label>
              <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.opportunities}</p>
            </div>
          )}
          {risk.actions && (
            <div className="mb-3">
              <label className="text-sm font-medium text-gray-500">Actions</label>
              <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.actions}</p>
            </div>
          )}
          {risk.comments && (
            <div>
              <label className="text-sm font-medium text-gray-500">Comments</label>
              <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.comments}</p>
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Status & Review</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Control Status</label>
            <p className="text-gray-900">{risk.controlStatus}</p>
          </div>
          {risk.dateCompleted && (
            <div>
              <label className="text-sm font-medium text-gray-500">Implementation Date</label>
              <p className="text-gray-900">{formatDate(risk.dateCompleted)}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-500">Last Reviewed</label>
            <p className="text-gray-900">{risk.lastDateReviewed ? formatDate(risk.lastDateReviewed) : '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Review Period</label>
            <p className="text-gray-900">{risk.maxReviewPeriod} months</p>
          </div>
        </div>
      </div>

      {risk.notes && (
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-4">Notes</h3>
          <p className="text-gray-900 bg-gray-50 p-3 rounded">{risk.notes}</p>
        </div>
      )}
    </div>
  );
}
